import { Injectable } from '@angular/core';
import { Store as ngxsStore } from '@ngxs/store';
import { Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import * as signalR from '@microsoft/signalr';
import { Organization } from 'app/models/organization.model';
import { OrganizationsState } from 'app/state-management/states/organizations.states';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest, firstValueFrom } from 'rxjs';
import { selectTokenInfo } from "app/reducers/context.selectors";
import { refreshLoginData, signOutUser } from 'app/reducers/context.actions';
import { ApiSignalUrl } from 'app/services/http/url-constants/hub-url-constants';
import { TokenInfo } from 'app/reducers/context.reducer';
import { APIService } from 'app/services/api.service';
import { EventService } from 'app/services/signalR/event.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class SignalrService {
  private connection: signalR.HubConnection;
  private isTryConnection: boolean;
  private isConnected: boolean;
  private lastHashSelectedOrganizations = '';

  constructor(
    private ngxsStore: ngxsStore,
    private store: Store,
    private apiService: APIService,
    private eventService: EventService,
  ) {
  }

  public async initAsync(): Promise<void>
  {
    combineLatest(
      [
        this.store.select(selectTokenInfo),
        this.ngxsStore.select(OrganizationsState.getSelectedOrganizationTree),
      ]
    ).pipe(
      untilDestroyed(this),
    ).subscribe(async ([tokenInfo, selectedOrganizations]): Promise<void> => {
      if (!tokenInfo || !tokenInfo.token) {
        await this.disconnectAsync();
        return;
      }

      if (!this.isConnected) {
        await this.connectAsync(tokenInfo);
      }

      await this.subscribeOrganizationsAsync(selectedOrganizations);
    });
  }

  public async disconnectAsync(): Promise<void> {
    console.log('disconnect');
    if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
      return await this.connection.stop();
    }
    this.isConnected = false;
    return;
  }

  private isDisconnected(): boolean {
    return !this.connection || this.connection.state === signalR.HubConnectionState.Disconnected
  }

  private async connectAsync(tokenInfo: TokenInfo): Promise<void> {
    if (!this.connection || this.isDisconnected()) {
      this.connection = this.createConnection(tokenInfo);
      console.log('connect');
    }

    if (this.isTryConnection || !this.isDisconnected())
    {
      return;
    }
    this.isTryConnection = true;

    try {
      await this.connection.start();

      this.eventService.registerEvents(this.connection);

      this.isConnected = true;
    }
    catch (connectionError) {
      if (connectionError.message && connectionError.message?.includes('Status code \'401\''))
      {
        try {
          const refreshTokenResponse = await firstValueFrom(this.apiService.refreshToken({refreshToken: tokenInfo.refreshToken}));
          const tokenParts = this.apiService.decodeToken(refreshTokenResponse.data.authToken);

          const newTokenInfo: TokenInfo = {
            token: refreshTokenResponse.data.authToken,
            refreshToken: refreshTokenResponse.data.refreshToken,
            tokenExpires: tokenParts.tokenExpires,
          };

          this.store.dispatch(refreshLoginData({
            tokenInfo: newTokenInfo,
            userData: refreshTokenResponse.data.userData
          }));
          await this.connectAsync(newTokenInfo);
        }
        catch (refreshError){
          await this.disconnectAsync();
          this.store.dispatch(signOutUser());
        }
      }
      else{
        console.log('Error while starting connection: ', connectionError);
        setTimeout(() => this.connectAsync(tokenInfo), 5000);
      }
    }
    finally {
      this.isTryConnection = false;
    }
  }

  private createConnection(tokenInfo: TokenInfo): signalR.HubConnection
  {
    return new signalR.HubConnectionBuilder()
      .withUrl(`${environment.baseUrl}/${ApiSignalUrl}`, {accessTokenFactory: () => tokenInfo.token})
      .build();
  }

  private async subscribeOrganizationsAsync(selectedOrganizations: Organization[]): Promise<void>
  {
    const selectedOrganizationsIds = selectedOrganizations.map((organization) => organization.id);

    if (selectedOrganizationsIds.length && selectedOrganizationsIds.join('|') !== this.lastHashSelectedOrganizations) {
      this.lastHashSelectedOrganizations = selectedOrganizationsIds.join('|');
      await this.connection.invoke('OrganizationSubscribe', selectedOrganizationsIds);
    }
  }
}
